import { Box,Container, Card, Input, InputAdornment, Menu, CircularProgress, Link, Button, Select, Typography, Stack, MenuItem, IconButton, Tooltip, FormLabel, FormControl, Divider, RadioGroup, FormControlLabel, Radio, Checkbox, Popover } from "@mui/material";
import { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { MapTypeSelector, useMapTypeSelector } from "src/pages/app/eld/eld-landmarks/eld-landmark-map-modal";
import L from "leaflet";
import 'leaflet-providers';
import 'leaflet/dist/leaflet.css';
import Logo from 'src/components/logo';
import NoneFound from "src/components/none-found";
import { useParams } from "react-router";
import { Device } from "src/models";
import axios, { endpoints, formatEndpoint } from 'src/utils/axios';
import useModelData from "src/hooks/use-model-data";
import { DisplayPageData } from "src/components/display-page-data";
import { useSettingsContext } from 'src/components/settings';
import { usePrevious } from "src/hooks/use-previous";
import Iconify from "src/components/iconify";
import { useLocalStorage } from "src/hooks/use-local-storage";
import { APIProvider } from "@vis.gl/react-google-maps";
import { GOOGLE_MAPS_API_KEY } from "src/config-global";
import { GoogleMapWrapper, MAP_TYPES, MarkerClustererComponent, MarkerData, TrafficLayer, WeighStationsLayer } from "../app/devices/map-google";

export const MAP_OVERLAYS = [
  { key: 'traffic', label: 'Traffic' },
  { key: 'weighStation', label: 'Weigh Stations' },
] as const;

type PageModel = {
  deviceDataResponse: {device?: Device, devices?: Device[], organizationName?: string} | {message: string},
}

export default function Page() {
  const settings = useSettingsContext();
  const { shareLinkUuid } = useParams();

  const dataLoader = useCallback(() => ({
    deviceDataResponse: axios.get(formatEndpoint(endpoints.devices.findByShareLinkUuid, {shareLinkUuid})).then(response => response.data),
  }), [shareLinkUuid]);

  const {
    data,
    updateData,
    setData,
    loaded,
    error,
    loading,
    setShouldRefresh,
  } = useModelData<PageModel>(dataLoader);


  return <Container maxWidth={settings.themeStretch ? false : 'lg'} sx={{p: 0}}>
    {data && !('message' in data.deviceDataResponse) && <Box height="55px" sx={{display: "flex", gap: 2, justifyContent: "space-between", alignItems: "center"}}>
      <Logo dark sx={{alignItems: "center"}} />
      <Box sx={{fontWeight: 800}}>{data.deviceDataResponse.organizationName ? data.deviceDataResponse.organizationName : ""}</Box>
    </Box>}
    <DisplayPageData loading={loading} error={error} data={data}>
      {data && <>
        {'message' in data.deviceDataResponse
          ? <Box p={4}>
            <NoneFound title={data.deviceDataResponse.message} message="Please contact the owner of the Account">
              <Logo dark />
            </NoneFound>
          </Box>
          :
            <Box position="absolute" left="0px" top="55px" right="0px" bottom="-15px" display="flex" flexDirection="row">
              <APIProvider apiKey={GOOGLE_MAPS_API_KEY}>
                <DeviceMapGoogle devices={data.deviceDataResponse.device ? [data.deviceDataResponse.device] : data.deviceDataResponse.devices!}/>
              </APIProvider>
            </Box>
           }
      </>}
    </DisplayPageData>
  </Container>;
}



function DeviceMapGoogle({ devices }: { devices: Device[]}) {
  const [openInfoWindowUuid, setOpenInfoWindowUuid] = useState<string | null>(null);
  const [isFullscreen, setIsFullscreen] = useState<boolean>(false);

  const mapRef = useRef<google.maps.Map | null>(null);
  const [selectedUuid, setSelectedUuid] = useState("");
  const [mapOptionsOpen, setMapOptionsOpen] = useState<HTMLElement | null>(null);
    const { state: mapSettings, update: updateMapSettings } = useLocalStorage('trux-map-settings', {
      mapType: 'default',
      selectedOptions: []
    });
  const {selectedOptions} = mapSettings;
  const setSelectedOptions = (options: string[]) => updateMapSettings('selectedOptions', options);
  const {mapType} = mapSettings;
  const setMapType = (type: string) => updateMapSettings('mapType', type);

  const selectedUuidPrev = usePrevious(selectedUuid);
  // Prepare marker data
  const markers: MarkerData[] = useMemo(() => devices
      .filter(device => device.lastPosLat && device.lastPosLong && device.lastPing)
      .map(device => ({
        device: {...device, location: ""},
        position: {
          lat: Number(device.lastPosLat),
          lng: Number(device.lastPosLong)
        }
      })), [devices]);

  // Open info window for selected device
  useEffect(() => {
    if (selectedUuid && selectedUuid !== selectedUuidPrev) {
      setOpenInfoWindowUuid(selectedUuid);
      const marker = markers.find(m => m.device.deviceUuid === selectedUuid);
      if (marker && mapRef.current) {
        mapRef.current.panTo(marker.position);
        if ((mapRef.current.getZoom() || 100) < 12) {
          mapRef.current.setZoom(12);
        }
      }
    }
  }, [selectedUuid, markers, mapRef, selectedUuidPrev]);

  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };

    document.addEventListener('fullscreenchange', handleFullscreenChange);
    document.addEventListener('webkitfullscreenchange', handleFullscreenChange);
    document.addEventListener('mozfullscreenchange', handleFullscreenChange);
    document.addEventListener('MSFullscreenChange', handleFullscreenChange);

    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
      document.removeEventListener('webkitfullscreenchange', handleFullscreenChange);
      document.removeEventListener('mozfullscreenchange', handleFullscreenChange);
      document.removeEventListener('MSFullscreenChange', handleFullscreenChange);
    };
  }, []);

  const onToggleFullScreen = useCallback(() => {
    if (!document.fullscreenElement) {
      document.documentElement.requestFullscreen();
    } else if (document.exitFullscreen) {
      document.exitFullscreen();
    }
  }, []);


  const handleMarkerClick = useCallback((device: Device) => {
    setSelectedUuid(device.deviceUuid);
  }, [setSelectedUuid]);

  return (
    <>
      <Box sx={{ flexGrow: 1, position: "relative" }}>
        <Box sx={{
          position: "absolute",
          top: "20px",
          right: "20px",
          zIndex: "1000",
          borderRadius: "4px",
          display: "flex",
          flexDirection: "column",
          alignItems: "end",
          gap: "10px"
        }}>
          <Box>
             <Tooltip title="Map Options" arrow>
               <Button
                 className="map-overlay-element"
                 variant="contained"
                 sx={{minWidth: "initial", background: "#FEFEFE", color: "black", "&:hover": {background: "#DEDEDE"}, boxShadow: "0 0 10px rgba(0,0,0,0.2)" }}
                 onClick={(e) => setMapOptionsOpen(e.currentTarget)}
               >
                 <Iconify icon="material-symbols-light:layers-outline" />
               </Button>
             </Tooltip>
           </Box>

          <Box>
            <Tooltip title="Toggle fullscreen" arrow>
              <Button
                className="map-overlay-element"
                variant="contained"
                onClick={onToggleFullScreen}
                sx={{minWidth: "initial", background: "#FEFEFE", color: "black", "&:hover": {background: "#DEDEDE"}, boxShadow: "0 0 10px rgba(0,0,0,0.2)" }}
              >
                <Iconify icon={isFullscreen ? "material-symbols-light:fullscreen-exit" : "material-symbols-light:fullscreen"} />
              </Button>
            </Tooltip>
          </Box>
        </Box>


        {/* Zoom controls */}
        <Box sx={{
          position: "absolute",
          top: "20px",
          left: "20px",
          zIndex: "1000",
          background: "white",
          borderRadius: "3px",
          boxShadow: "0 0 10px rgba(0,0,0,0.2)",
          border: "1px solid rgba(145, 158, 171, 0.32)",
          display: "flex",
          flexDirection: "column",
        }}>
          <IconButton size="small" onClick={() => {
            if (mapRef.current) {
              const currentZoom = mapRef.current.getZoom();
              if (currentZoom !== undefined) {
                mapRef.current.setZoom(currentZoom + 1);
              }
            }
          }}>
            <Iconify icon="mdi:plus" />
          </IconButton>
          <Box sx={{ borderTop: "1px solid #e0e0e0" }} />
          <IconButton size="small" onClick={() => {
            if (mapRef.current) {
              const currentZoom = mapRef.current.getZoom();
              if (currentZoom !== undefined) {
                mapRef.current.setZoom(currentZoom - 1);
              }
            }
          }}>
            <Iconify icon="mdi:minus" />
          </IconButton>
        </Box>

        {/* Map */}
        <GoogleMapWrapper
          mapType={mapType}
          mapRef={mapRef}
        >

          {/* Weigh stations layer */}
          {selectedOptions.includes("weighStation") && (
            <WeighStationsLayer />
          )}

          {selectedOptions.includes("traffic") && (
            <TrafficLayer />
          )}

          {/* Device markers with clustering */}
          <MarkerClustererComponent
            deviceEldDrivers={[]}
            markersData={markers}
            openInfoWindowUuid={openInfoWindowUuid}
            setOpenInfoWindowUuid={setOpenInfoWindowUuid}
            timezone="America/Chicago"
            eldConnectedVehicleUnitNumbers={[]}
            onTruckMarkerClick={handleMarkerClick}
          />
        </GoogleMapWrapper>
      </Box>

      {/* Map Options Menu */}
      <Popover
        open={Boolean(mapOptionsOpen)}
        anchorEl={mapOptionsOpen}
        onClose={() => setMapOptionsOpen(null)}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        PaperProps={{
          sx: {
            minWidth: 200,
            maxWidth: 320,
            p: 2,
            borderRadius: 0.8,
            boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
            marginTop: "3px"
          }
        }}
      >
        <Stack spacing={2}>
          {/* Map Type Selection */}
          <FormControl component="fieldset">
            <FormLabel sx={{
              fontWeight: 600,
              color: 'text.secondary',
              mb: 1,
              '&.Mui-focused': {
                color: 'text.secondary'
              },
              '&.MuiFormLabel-filled': {
                color: 'text.secondary'
              }
            }}>
              MAP BASE
            </FormLabel>
              <RadioGroup
                value={mapType}
                onChange={(e) => setMapType(e.target.value as 'default' | 'satellite' | 'terrain')}
                sx={{ paddingLeft: "2px" }}
              >
                {MAP_TYPES.map((type) => (
                  <FormControlLabel
                    key={type.key}
                    value={type.key}
                    control={<Radio size="small" />}
                    label={type.label}
                  />
                ))}
              </RadioGroup>
          </FormControl>

          <Divider />

          {/* Overlay Options */}
          <FormControl component="fieldset">
            <FormLabel sx={{
              fontWeight: 600,
              color: 'text.secondary',
              mb: 2,
              '&.Mui-focused': {
                color: 'text.secondary'
              },
              '&.MuiFormLabel-filled': {
                color: 'text.secondary'
              }
            }}>
              OVERLAYS
            </FormLabel>
            <Stack spacing={2} paddingLeft="8px">
              {MAP_OVERLAYS.map((overlay) => (
                <FormControlLabel
                  key={overlay.key}
                  control={
                    <Checkbox
                      size="small"
                      sx={{ paddingX: "6px" }}
                      checked={selectedOptions.includes(overlay.key)}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setSelectedOptions([...selectedOptions, overlay.key]);
                        } else {
                          setSelectedOptions(selectedOptions.filter((option: string) => option !== overlay.key));
                        }
                      }}
                    />
                  }
                  label={overlay.label}
                />
              ))}
            </Stack>
          </FormControl>
        </Stack>
      </Popover>
    </>
  );
}